import { memo, useEffect, useRef } from 'react';
import { createParticleEngine, type AmbientEmitterHandle, type ParticleEngine } from './engine.js';
import { PRESETS, type PresetName } from './presets.js';
import { useDeviceTier } from '../useDeviceTier.js';
import { useReducedMotion } from './useReducedMotion.js';

/**
 * `<ParticleLayer>` — an absolutely-positioned canvas that fills its parent and
 * runs one ambient particle preset. Non-interactive (`pointer-events:none`,
 * `aria-hidden`), memoized, and driven entirely by primitive props so it never
 * re-mounts the engine on an unrelated parent render.
 *
 * The parent must be positioned (relative/absolute) — the canvas pins to it.
 * Intensity scales the ambient target count (pre-device-cap); the engine still
 * enforces the tier's hard particle ceiling on top.
 */

export interface ParticleLayerProps {
  /** Which ambient preset to run. */
  preset: PresetName;
  /** 0..1 multiplier on the preset's target count. Default 1. */
  intensity?: number;
  /** Pause the layer without unmounting (e.g. game paused). Default false. */
  paused?: boolean;
  /**
   * Optional stacking hint passed through to the canvas z-index. Layers with a
   * higher slot render above lower ones within the same parent.
   */
  slot?: number;
  /**
   * Multiplier on the preset's particle size range (surface-level tuning — e.g.
   * the Discord desktop surface runs bigger particles than a phone). Default 1.
   */
  sizeScale?: number;
  /**
   * Multiplier on the ambient target count, applied AFTER intensity (which is
   * clamped 0..1). Lets a big desktop surface run denser beds than the preset's
   * phone-calibrated count. The engine's device cap still applies. Default 1.
   */
  countScale?: number;
}

function ParticleLayerImpl({
  preset,
  intensity = 1,
  paused = false,
  slot = 0,
  sizeScale = 1,
  countScale = 1,
}: ParticleLayerProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const engineRef = useRef<ParticleEngine | null>(null);
  const emitterRef = useRef<AmbientEmitterHandle | null>(null);
  const reduced = useReducedMotion();
  const { caps } = useDeviceTier();

  // Mount the engine + emitter once. Reduced-motion mounts nothing.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || reduced) return;

    const engine = createParticleEngine(canvas, {
      dprCap: caps.dprCap,
      maxParticles: caps.maxParticles,
    });
    engineRef.current = engine;

    const def = scaledPreset(preset, sizeScale);
    const target = Math.round(def.count * clamp01(intensity) * countScale);
    emitterRef.current = engine.addAmbient(def, target);

    return () => {
      emitterRef.current = null;
      engine.destroy();
      engineRef.current = null;
    };
    // Preset/sizeScale change re-mounts the engine (different emitter identity);
    // intensity is applied via the effect below without a re-mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preset, sizeScale, reduced, caps.dprCap, caps.maxParticles]);

  // Intensity/countScale → emitter target (no re-mount).
  useEffect(() => {
    const def = PRESETS[preset];
    emitterRef.current?.setTarget(Math.round(def.count * clamp01(intensity) * countScale));
  }, [preset, intensity, countScale]);

  // Paused → engine pause.
  useEffect(() => {
    engineRef.current?.setPaused(paused);
  }, [paused]);

  return (
    <canvas
      ref={canvasRef}
      aria-hidden
      style={{
        position: 'absolute',
        inset: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
        zIndex: slot,
      }}
    />
  );
}

function clamp01(n: number): number {
  return n < 0 ? 0 : n > 1 ? 1 : n;
}

/** Preset with its size range multiplied — identity-stable per (preset, scale). */
function scaledPreset(name: PresetName, scale: number) {
  const def = PRESETS[name];
  if (scale === 1) return def;
  return { ...def, size: [def.size[0] * scale, def.size[1] * scale] as [number, number] };
}

export const ParticleLayer = memo(ParticleLayerImpl);
